import { ExpandableFilter } from './expandable-filter';
import { Headphones } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MonthlyListenersFilterProps {
  value: string;
  onChange: (value: string) => void;
  facets?: Array<{
    bucket: 'under-100k' | '100k-500k' | '500k-1m' | 'over-1m';
    count: number;
  }>;
}

const LISTENER_BUCKETS = [
  { value: 'all', label: 'all_listeners' },
  { value: 'under-100k', label: 'under_100k' },
  { value: '100k-500k', label: '100k_to_500k' },
  { value: '500k-1m', label: '500k_to_1m' },
  { value: 'over-1m', label: 'over_1m' },
];

export function MonthlyListenersFilter({ value, onChange, facets }: MonthlyListenersFilterProps) {
  const { t } = useTranslation('collab-club');

  // Build options list, filtering out those with count === 0
  const options = LISTENER_BUCKETS.filter((b) => {
    // Always show "all" option
    if (b.value === 'all') return true;
    // Always show the currently selected option
    if (b.value === value) return true;
    // If no facets available, show all options
    if (!facets) return true;
    // Find the facet for this bucket
    const facet = facets.find((f) => f.bucket === b.value);
    // Hide if count is exactly 0, show if not found (null/undefined) or > 0
    return !facet || facet.count !== 0;
  }).map((b) => b.value);

  // Custom render function for options with counts
  const renderOption = (option: string) => {
    const bucket = LISTENER_BUCKETS.find((b) => b.value === option);
    const label = bucket ? t(bucket.label) : option;

    // Add count if facets are available (but not for "all")
    if (facets && option !== 'all') {
      const facet = facets.find((f) => f.bucket === option);
      const count = facet ? facet.count : 0;
      return (
        <span className="flex items-center justify-between w-full">
          <span>{label}</span>
          <span className="text-xs text-gray-500">{count}</span>
        </span>
      );
    }

    return label;
  };

  return (
    <ExpandableFilter
      title={t('monthly_listeners')}
      icon={<Headphones className="h-4 w-4" />}
      options={options}
      selectedValue={value || 'all'}
      onSelectionChange={onChange}
      initialLimit={10}
      renderOption={renderOption}
    />
  );
}
